<template>
  <section class="chain-deposits" :class="platform" data-t="chain-deposits">
    <div class="chain-info">
      <div class="main-info">
        <h2 class="title">{{ t('bonuses.bonus') }}</h2>
        <p class="multiplier" data-t="sum-multiplier">
          {{ chain.totalDepositBonusPercent }}
        </p>
      </div>
      <p class="max-bonus" data-t="max-bonus">
        {{ t('bonuses.toAmount') }}
        {{ chain.totalDepositBonusAmountInBonusCurrency }}
        <StIcon
          v-if="chain.bonusCurrencyIcon"
          :size="isMobile ? 16 : 18"
          :name="chain.bonusCurrencyIcon"
        />
      </p>

      <StButton
        v-if="!isMobile"
        class="button"
        size="l"
        type="primary"
        :to="{
          query: {
            modal: 'payments',
            tab: 'deposit',
          },
        }"
        :label="t('bonuses.makeDeposit')"
      />
    </div>
    <div class="chain">
      <DepositChainItem
        v-for="level in chain.chainLevels"
        :key="level.id"
        :chain-level="level"
        :is-default="isDefault"
      />
    </div>
    <StButton
      v-if="isMobile"
      class="button"
      size="l"
      type="primary"
      :to="{
        query: {
          modal: 'payments',
          tab: 'deposit',
        },
      }"
      :label="t('bonuses.makeDeposit')"
    />
  </section>
</template>

<script setup lang="ts">
import type { DepositBonusesPreparedChain } from '../../types'
import DepositChainItem from './parts/DepositChainItem.vue'

const { t } = useI18n()

interface Props {
  chain: DepositBonusesPreparedChain
  isDefault?: boolean
}

const { chain, isDefault = false } = defineProps<Props>()

const { platform, isMobile } = usePlatform()
</script>

<style scoped>
.chain-info {
  position: relative;

  display: flex;
  flex-direction: column;
  gap: var(--spacing-075);
  align-self: stretch;

  min-width: 224px;
  max-width: 240px;
  min-height: 148px;
  max-height: 306px;

  &::after {
    content: '';

    position: absolute;
    top: 0;
    right: -32px;
    bottom: 0;

    display: block;

    width: 1px;

    background: var(--border-primary);
  }
}

.main-info {
  display: flex;
  gap: var(--spacing-075);
  align-items: flex-end;
}

.title {
  margin: 0;
  font: var(--desktop-text-xl-semibold);
  color: var(--text-primary);
}

.multiplier {
  margin: 0;
  font: var(--desktop-text-3xl-semibold);
  color: var(--text-warning);
  text-shadow:
    0 24px 64px rgb(0 0 0 / 40%),
    0 8px 16px rgb(0 0 0 / 20%);
}

.max-bonus {
  display: flex;
  gap: var(--spacing-050);
  align-items: center;

  margin: 0;

  font: var(--desktop-text-lg-medium);
  color: var(--text-secondary);
}

.button {
  margin-top: auto;
}

.chain {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  flex-grow: 1;
  gap: var(--spacing-200);

  padding-top: var(--spacing-200);
  padding-bottom: var(--spacing-100);
}

.chain-deposits {
  display: flex;
  gap: var(--spacing-800);
  align-items: flex-start;

  padding: var(--spacing-250) var(--spacing-400) var(--spacing-350)
    var(--spacing-400);

  background: linear-gradient(91deg, #711528 0%, #1f1f29 50.41%);
  border-radius: var(--border-radius-200);

  &.mobile {
    flex-direction: column;
    gap: var(--spacing-200);
    align-items: stretch;

    width: 100%;
    padding: var(--spacing-125) var(--spacing-200) var(--spacing-200);

    background: linear-gradient(180deg, #711528 0%, #1f1f29 50.41%);
    border-radius: var(--border-radius-150);

    .chain {
      grid-template-columns: 1fr;
      gap: var(--spacing-050);
      padding: 0;
    }

    .chain-info {
      display: flex;
      gap: var(--spacing-025);

      max-width: 100%;
      min-height: auto;
      max-height: unset;

      &::after {
        display: none;
      }
    }

    .main-info {
      display: flex;
      gap: var(--spacing-050);
      align-items: center;
      justify-content: center;
    }

    .title {
      font: var(--mobile-headline-semibold);
    }

    .multiplier {
      font: var(--desktop-text-xl-semibold);
    }

    .max-bonus {
      justify-content: center;
      font: var(--mobile-text-medium);
    }
  }
}
</style>
